/* Native navigation remains usable without JavaScript. No customer/cart data leaves Shopify. */
(() => {
  const motionPreferenceKey = 'fff-motion-preference';
  try {
    const storedMotion = localStorage.getItem(motionPreferenceKey);
    if (storedMotion === 'off' || storedMotion === 'on') document.body.classList.toggle('motion-off', storedMotion === 'off');
  } catch (_) { /* Motion controls still work when storage is unavailable. */ }
  if (!customElements.get('fff-motion-toggle')) {
    customElements.define('fff-motion-toggle', class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.controller = new AbortController();
        const signal = this.controller.signal;
        const button = this.querySelector('button');
        if (!button) return;
        const systemMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = () => {
          const enabled = !systemMotion.matches && !document.body.classList.contains('motion-off');
          button.hidden = false;
          button.textContent = systemMotion.matches ? 'Motion: off (system)' : `Motion: ${enabled ? 'on' : 'off'}`;
          button.setAttribute('aria-pressed', String(enabled));
          button.setAttribute('aria-label', systemMotion.matches ? 'Motion disabled by your device setting' : 'Enable animations');
          button.disabled = systemMotion.matches;
        };
        button.addEventListener('click', () => {
          const off = !document.body.classList.contains('motion-off');
          document.body.classList.toggle('motion-off', off);
          try { localStorage.setItem(motionPreferenceKey, off ? 'off' : 'on'); } catch (_) { /* Preference is applied for this page. */ }
          document.dispatchEvent(new CustomEvent('fff:motion-change', { detail: { enabled: !off } }));
          update();
        }, { signal });
        systemMotion.addEventListener('change', update, { signal });
        document.addEventListener('fff:motion-change', update, { signal });
        update();
      }
      disconnectedCallback() { this.controller?.abort(); this.controller = null; }
    });
  }
  if (customElements.get('fff-site-header')) return;
  class FFFSiteHeader extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.setAttribute('data-enhanced', '');
      const menuButton = this.querySelector('.fff-menu-toggle');
      const groups = Array.from(this.querySelectorAll('.fff-nav-group'));
      const desktop = window.matchMedia('(min-width: 990px)');
      const closeMenu = (restoreFocus = false) => {
        const activeGroup = groups.find(group => group.open);
        const mobileWasOpen = this.hasAttribute('data-menu-open');
        groups.forEach(group => { group.open = false; });
        this.removeAttribute('data-menu-open');
        menuButton?.setAttribute('aria-expanded', 'false');
        menuButton?.setAttribute('aria-label', 'Open menu');
        const glyph = menuButton?.querySelector('.fff-menu-glyph');
        if (glyph) glyph.textContent = '+';
        if (restoreFocus) {
          if (mobileWasOpen && !desktop.matches) menuButton?.focus();
          else activeGroup?.querySelector('summary')?.focus();
        }
      };
      menuButton?.addEventListener('click', () => {
        if (this.hasAttribute('data-menu-open')) { closeMenu(); return; }
        this.setAttribute('data-menu-open', '');
        menuButton.setAttribute('aria-expanded', 'true');
        menuButton.setAttribute('aria-label', 'Close menu');
        menuButton.querySelector('.fff-menu-glyph').textContent = '−';
      }, { signal });
      groups.forEach(group => group.addEventListener('toggle', () => {
        if (group.open) groups.forEach(other => { if (other !== group) other.open = false; });
      }, { signal }));
      document.addEventListener('click', event => { if (!this.contains(event.target)) closeMenu(); }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && (this.hasAttribute('data-menu-open') || groups.some(group => group.open))) {
          event.preventDefault();
          event.stopPropagation();
          closeMenu(true);
        }
      }, { signal });
      this.addEventListener('focusout', event => {
        if (desktop.matches && event.relatedTarget && !this.contains(event.relatedTarget)) closeMenu();
      }, { signal });
      desktop.addEventListener('change', () => closeMenu(), { signal });
      this.startLogo(signal);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.logoObserver?.disconnect();
      this.querySelector('video')?.pause();
    }
    startLogo(signal) {
      const mark = this.querySelector('.fff-metal-logo');
      const video = mark?.querySelector('video');
      const poster = mark?.querySelector('img');
      if (!video || !poster) return;
      const key = 'fff-metal-header-intro-v1';
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let attempted = false;
      let playbackAllowed = false;
      try { attempted = sessionStorage.getItem(key) === 'seen'; } catch (_) { /* Storage can be unavailable. */ }
      const reduced = () => motion.matches || document.body.classList.contains('motion-off');
      const showStill = () => {
        playbackAllowed = false;
        video.pause();
        mark.classList.remove('is-playing');
      };
      const start = async () => {
        if (attempted || reduced() || document.hidden || !this.isConnected) return;
        attempted = true;
        try {
          if (poster.decode) await poster.decode();
          if (signal.aborted) return;
          if (reduced() || document.hidden) { attempted = false; return; }
          playbackAllowed = true;
          video.src = video.dataset.src;
          video.muted = true;
          video.loop = false;
          await video.play();
        } catch (_) { showStill(); }
      };
      video.addEventListener('playing', () => {
        if (!playbackAllowed || reduced() || document.hidden) { showStill(); return; }
        mark.classList.add('is-playing');
        try { sessionStorage.setItem(key, 'seen'); } catch (_) { /* Static fallback does not depend on storage. */ }
      }, { signal });
      video.addEventListener('ended', showStill, { signal });
      video.addEventListener('error', showStill, { signal });
      motion.addEventListener('change', () => { if (reduced()) showStill(); else start(); }, { signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) showStill(); else start(); }, { signal });
      this.logoObserver = new MutationObserver(() => { if (reduced()) showStill(); else start(); });
      this.logoObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      start();
    }
  }
  customElements.define('fff-site-header', FFFSiteHeader);
})();
